import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DateFilter from '../components/DateFilter';
import { parseDateFilterFromURL, serializeDateFilterToURL, buildAPIParams } from '../utils/dateFilterUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Parse date filter from URL, default to This Month with Due Date basis
  const [dateFilter, setDateFilter] = useState(() => parseDateFilterFromURL(searchParams));

  useEffect(() => {
    fetchData();
  }, [dateFilter]);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    // Update URL params
    setSearchParams(serializeDateFilterToURL(newFilter));
  };

  const fetchData = async () => {
    try {
      const params = buildAPIParams(dateFilter);
      const statsUrl = `${API}/dashboard/stats${params ? `?${params}` : ''}`;
      const invoicesUrl = `${API}/invoices${params ? `?${params}` : ''}`;

      const [statsRes, invoicesRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(invoicesUrl)
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  const agingData = stats?.aging ? [
    { name: '0-30 Days', value: stats.aging['0-30'], fill: '#0F172A' },
    { name: '31-60 Days', value: stats.aging['31-60'], fill: '#F97316' },
    { name: '61-90 Days', value: stats.aging['61-90'], fill: '#64748B' },
    { name: '90+ Days', value: stats.aging['90+'], fill: '#EF4444' },
  ] : [];

  const statusData = [
    { name: 'Pending', value: invoices.filter(inv => inv.status === 'pending').length, fill: '#F59E0B' },
    { name: 'Paid', value: invoices.filter(inv => inv.status === 'paid').length, fill: '#10B981' },
    { name: 'Overdue', value: invoices.filter(inv => inv.status === 'overdue').length, fill: '#EF4444' },
    { name: 'Partial', value: invoices.filter(inv => inv.status === 'partial').length, fill: '#3B82F6' },
  ].filter(item => item.value > 0);

  const monthlyData = {};
  invoices.forEach(inv => {
    const month = new Date(inv.created_at).toLocaleString('default', { month: 'short' });
    if (!monthlyData[month]) {
      monthlyData[month] = { month, total: 0, paid: 0 };
    }
    monthlyData[month].total += inv.amount;
    if (inv.status === 'paid') {
      monthlyData[month].paid += inv.amount;
    }
  });

  const monthlyChartData = Object.values(monthlyData);

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8" data-testid="reports-page">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="mb-4">
            <h1 className="font-heading text-2xl md:text-4xl text-slate-900 mb-1 md:mb-2">Reports & Analytics</h1>
            <p className="text-sm md:text-base text-slate-600">Detailed insights into collections performance</p>
          </div>

          {/* Date Filter */}
          <DateFilter
            preset={dateFilter.preset}
            basis={dateFilter.basis}
            startDate={dateFilter.startDate}
            endDate={dateFilter.endDate}
            onChange={handleDateFilterChange}
          />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6 md:mb-8">
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="report-outstanding">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs md:text-sm text-slate-500 mb-1 uppercase tracking-wide">Outstanding</p>
              <p className="font-mono-data text-2xl md:text-3xl font-bold text-slate-900">
                ${stats?.total_outstanding?.toLocaleString() || '0'}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="report-overdue">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs md:text-sm text-slate-500 mb-1 uppercase tracking-wide">Overdue</p>
              <p className="font-mono-data text-2xl md:text-3xl font-bold text-orange-600">
                ${stats?.total_overdue?.toLocaleString() || '0'}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="report-collected">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs md:text-sm text-slate-500 mb-1 uppercase tracking-wide">Collected</p>
              <p className="font-mono-data text-2xl md:text-3xl font-bold text-green-600">
                ${stats?.total_paid_this_month?.toLocaleString() || '0'}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="report-invoices">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs md:text-sm text-slate-500 mb-1 uppercase tracking-wide">Total Invoices</p>
              <p className="font-mono-data text-2xl md:text-3xl font-bold text-slate-900">
                {stats?.total_invoices || '0'}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Aging Report */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="aging-chart">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl">Aging Report</CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Outstanding by days overdue</p>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              {agingData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={agingData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '0',
                        fontFamily: 'JetBrains Mono, monospace'
                      }}
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Bar dataKey="value" fill="#0F172A" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  No overdue invoices
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="status-chart">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl">Invoice Status</CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Distribution by status</p>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '0'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  No invoice data
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Performance */}
          {monthlyChartData.length > 0 && (
            <Card className="border border-slate-200 rounded-none shadow-none lg:col-span-2" data-testid="monthly-chart">
              <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
                <CardTitle className="font-heading text-lg md:text-xl">Monthly Performance</CardTitle>
                <p className="text-xs md:text-sm text-slate-600 mt-1">Total vs. Collected amounts</p>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '0',
                        fontFamily: 'JetBrains Mono, monospace'
                      }}
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Bar dataKey="total" fill="#0F172A" name="Total" />
                    <Bar dataKey="paid" fill="#10B981" name="Collected" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Reports;